import { useMemo, useRef, useState } from "react";
import { useGetIngredientsQuery } from "@/redux/services/inventory/ingredient.api";
import { useGetRestaurantManagersQuery } from "@/redux/services/user/user.api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AsyncCombobox from "@/components/inventory/AsyncCombobox";
import { GetStockAdjustmentsParams } from "@/redux/services/inventory/stockAdjustment.api";
import {
  Ingredient,
  StockAdjustmentReason,
} from "@/redux/services/inventory/inventory.type";
import { User } from "@/redux/services/user/user.type";
import {
  formatPhoneNumber,
  getStockAdjustmentReasonLabel,
  cn,
} from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { X } from "lucide-react";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";

export type AdjustmentFiltersValue = Omit<GetStockAdjustmentsParams, "page">;

interface AdjustmentFiltersProps {
  restaurantId: string;
  value: AdjustmentFiltersValue;
  onChange: (next: AdjustmentFiltersValue) => void;
}

interface DatePreset {
  id: string;
  label: string;
  from: Date;
  to: Date;
}

const buildPresets = (): DatePreset[] => {
  const now = new Date();
  return [
    {
      id: "today",
      label: "Aujourd'hui",
      from: startOfDay(now),
      to: endOfDay(now),
    },
    {
      id: "week",
      label: "Cette semaine",
      from: startOfWeek(now, { weekStartsOn: 1 }),
      to: endOfWeek(now, { weekStartsOn: 1 }),
    },
    {
      id: "month",
      label: "Ce mois",
      from: startOfMonth(now),
      to: endOfMonth(now),
    },
    {
      id: "lastMonth",
      label: "Dernier mois",
      from: startOfMonth(subMonths(now, 1)),
      to: endOfMonth(subMonths(now, 1)),
    },
  ];
};

const REASONS: StockAdjustmentReason[] = [
  "delivery",
  "waste",
  "correction",
  "return",
  "order_deduction",
  "cost_correction",
  "cancellation",
];

const SENTINEL_ALL = "__all__";

const userLabel = (u: User) =>
  u.fullName ?? (u.phone ? formatPhoneNumber(u.phone) : u.id);

const AdjustmentFilters = ({
  restaurantId,
  value,
  onChange,
}: AdjustmentFiltersProps) => {
  // Ingredient combobox — server-side ?name= search
  const [ingredientSearch, setIngredientSearch] = useState("");
  const debouncedIngredientSearch = useDebounce(ingredientSearch, 300);
  const { data: ingredientsData, isFetching: isFetchingIngredients } =
    useGetIngredientsQuery({
      restaurantId,
      query: debouncedIngredientSearch.trim()
        ? { name: debouncedIngredientSearch.trim() }
        : {},
    });
  const ingredients = ingredientsData?.results ?? [];
  const ingredientCache = useRef<Map<string, Ingredient>>(new Map());
  ingredients.forEach((i) => ingredientCache.current.set(i.id, i));

  // User combobox — no backend search, fetch all + filter client-side
  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounce(userSearch, 200);
  const { data: usersData, isFetching: isFetchingUsers } =
    useGetRestaurantManagersQuery({ restaurantId });
  const users = usersData?.results ?? [];
  const userCache = useRef<Map<string, User>>(new Map());
  users.forEach((u) => userCache.current.set(u.id, u));
  const filteredUsers = useMemo(() => {
    const q = debouncedUserSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const label = userLabel(u).toLowerCase();
      return label.includes(q);
    });
  }, [users, debouncedUserSearch]);

  const hasActiveFilters = Object.values(value).some(
    (v) => v !== undefined && v !== ""
  );

  const update = (patch: Partial<AdjustmentFiltersValue>) =>
    onChange({ ...value, ...patch });

  const clear = () =>
    onChange({
      reason: undefined,
      ingredientUuid: undefined,
      adjustedByUuid: undefined,
      from: undefined,
      to: undefined,
    });

  const ingredientOptions = [
    { value: SENTINEL_ALL, label: "Tous" },
    ...ingredients.map((i) => ({ value: i.id, label: i.name })),
  ];
  const userOptions = [
    { value: SENTINEL_ALL, label: "Tous" },
    ...filteredUsers.map((u) => ({ value: u.id, label: userLabel(u) })),
  ];

  const selectedIngredientLabel =
    value.ingredientUuid &&
    ingredientCache.current.get(value.ingredientUuid)?.name;
  const selectedUserId = value.adjustedByUuid;
  const selectedUser = selectedUserId
    ? userCache.current.get(selectedUserId)
    : undefined;
  const selectedUserLabel = selectedUser ? userLabel(selectedUser) : undefined;

  return (
    <div className="dash__card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-card-foreground">Filtres</h3>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1 text-xs font-medium text-danger hover:underline"
          >
            <X className="h-3.5 w-3.5" />
            Réinitialiser les filtres
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Période :</span>
        {buildPresets().map((preset) => {
          const isActive =
            value.from === preset.from.toISOString() &&
            value.to === preset.to.toISOString();
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() =>
                update({
                  from: preset.from.toISOString(),
                  to: preset.to.toISOString(),
                })
              }
              className={cn(
                "px-2.5 py-1 rounded-full text-xs font-medium transition-colors border",
                isActive
                  ? "bg-primary-light text-primary border-primary/30"
                  : "bg-muted text-muted-foreground border-transparent hover:bg-muted"
              )}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Raison
          </label>
          <Select
            value={value.reason ?? SENTINEL_ALL}
            onValueChange={(v) =>
              update({
                reason:
                  v === SENTINEL_ALL ? undefined : (v as StockAdjustmentReason),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SENTINEL_ALL}>Toutes</SelectItem>
              {REASONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {getStockAdjustmentReasonLabel(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Ingrédient
          </label>
          <AsyncCombobox
            value={value.ingredientUuid ?? SENTINEL_ALL}
            onChange={(v) =>
              update({
                ingredientUuid: v === SENTINEL_ALL ? undefined : v,
              })
            }
            options={ingredientOptions}
            selectedLabel={selectedIngredientLabel ?? "Tous"}
            placeholder="Tous"
            searchPlaceholder="Rechercher un ingrédient..."
            emptyMessage="Aucun ingrédient"
            isLoading={isFetchingIngredients}
            onSearchChange={setIngredientSearch}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Utilisateur
          </label>
          <AsyncCombobox
            value={value.adjustedByUuid ?? SENTINEL_ALL}
            onChange={(v) =>
              update({
                adjustedByUuid: v === SENTINEL_ALL ? undefined : v,
              })
            }
            options={userOptions}
            selectedLabel={selectedUserLabel ?? "Tous"}
            placeholder="Tous"
            searchPlaceholder="Rechercher un utilisateur..."
            emptyMessage="Aucun utilisateur"
            isLoading={isFetchingUsers}
            onSearchChange={setUserSearch}
          />
        </div>

        <div>
          <label
            htmlFor="adj-from"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Du
          </label>
          <input
            id="adj-from"
            type="date"
            value={value.from ? value.from.slice(0, 10) : ""}
            onChange={(e) =>
              update({
                from: e.target.value
                  ? `${e.target.value}T00:00:00Z`
                  : undefined,
              })
            }
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label
            htmlFor="adj-to"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Au
          </label>
          <input
            id="adj-to"
            type="date"
            value={value.to ? value.to.slice(0, 10) : ""}
            onChange={(e) =>
              update({
                to: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
              })
            }
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
};

export default AdjustmentFilters;
